import { useEffect, useRef, useState } from 'react'
import ReactCrop, { centerCrop, makeAspectCrop, type Crop, type PixelCrop } from 'react-image-crop'
import { MdClose, MdCrop } from 'react-icons/md'

import { cropImageToFile, fileToDataUrl, type CropOutput } from './crop-image'

import 'react-image-crop/dist/ReactCrop.css'

/** Proporção oferecida no diálogo; `value` vazio é corte livre. */
export interface CropAspectOption {
    label: string
    value?: number
}

export interface ImageCropDialogProps {
    /** Arquivo em edição; nulo fecha o diálogo. */
    file: File | null
    title?: string
    /** Proporção fixa (ex.: 1 para quadrada). Sem ela, vale `aspects` ou corte livre. */
    aspect?: number
    /** Opções de proporção para o lojista escolher (a primeira é a inicial). */
    aspects?: CropAspectOption[]
    /** Prévia redonda, para foto de perfil. */
    round?: boolean
    output?: CropOutput
    confirmLabel?: string
    onDone: (file: File) => void
    onCancel: () => void
}

function centeredCrop(width: number, height: number, aspect?: number): Crop {
    if (!aspect) return { unit: '%', x: 5, y: 5, width: 90, height: 90 }
    return centerCrop(makeAspectCrop({ unit: '%', width: 90 }, aspect, width, height), width, height)
}

/**
 * Diálogo de corte usado por todo campo de imagem do painel: o arquivo escolhido
 * passa por aqui antes do upload, e sai recortado, reduzido e comprimido
 * (ver `cropImageToFile`). Funciona com toque no celular (react-image-crop).
 */
export default function ImageCropDialog({
    file,
    title = 'Ajustar imagem',
    aspect,
    aspects,
    round = false,
    output,
    confirmLabel = 'Cortar e usar',
    onDone,
    onCancel,
}: ImageCropDialogProps) {
    const modalRef = useRef<HTMLDialogElement>(null)
    const imgRef = useRef<HTMLImageElement>(null)
    const [src, setSrc] = useState('')
    const [crop, setCrop] = useState<Crop>()
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
    const [currentAspect, setCurrentAspect] = useState<number | undefined>(aspect ?? aspects?.[0]?.value)
    const [processing, setProcessing] = useState(false)

    useEffect(() => {
        if (!file) {
            modalRef.current?.close()
            setSrc('')
            return
        }
        setCrop(undefined)
        setCompletedCrop(undefined)
        setCurrentAspect(aspect ?? aspects?.[0]?.value)
        let cancelled = false
        fileToDataUrl(file).then((url) => {
            if (!cancelled) setSrc(url)
        })
        modalRef.current?.showModal()
        return () => {
            cancelled = true
        }
        // A proporção inicial é recalculada por arquivo, não quando as props mudam.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file])

    const applyAspect = (value: number | undefined) => {
        setCurrentAspect(value)
        const img = imgRef.current
        if (img) setCrop(centeredCrop(img.width, img.height, value))
    }

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = event.currentTarget
        setCrop(centeredCrop(width, height, currentAspect))
    }

    const handleConfirm = async () => {
        const img = imgRef.current
        if (!file || !img || !completedCrop?.width || !completedCrop?.height) return
        setProcessing(true)
        try {
            onDone(await cropImageToFile(img, completedCrop, file.name, output))
        } finally {
            setProcessing(false)
        }
    }

    return (
        <dialog ref={modalRef} className="modal z-50" onCancel={(e) => (e.preventDefault(), onCancel())}>
            <div className="modal-box max-w-2xl p-0">
                <div className="flex items-center justify-between border-b border-base-200 p-4">
                    <h2 className="flex items-center gap-2 text-lg font-bold">
                        <MdCrop className="text-primary" />
                        {title}
                    </h2>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={onCancel}
                        disabled={processing}
                    >
                        <MdClose className="text-lg" />
                    </button>
                </div>

                <div className="flex flex-col gap-3 p-4">
                    {aspects && aspects.length > 1 && !aspect && (
                        <div className="flex flex-wrap gap-2">
                            {aspects.map((option) => (
                                <button
                                    key={option.label}
                                    type="button"
                                    className={`btn btn-xs ${currentAspect === option.value ? 'btn-primary' : 'btn-outline'}`}
                                    onClick={() => applyAspect(option.value)}
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex max-h-[60vh] items-center justify-center overflow-auto rounded-lg bg-base-200">
                        {src ? (
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(pixelCrop) => setCompletedCrop(pixelCrop)}
                                aspect={currentAspect}
                                circularCrop={round}
                                keepSelection
                                minHeight={24}
                                minWidth={24}
                            >
                                <img
                                    ref={imgRef}
                                    src={src}
                                    alt="Imagem para cortar"
                                    className="max-h-[56vh] w-auto"
                                    onLoad={handleImageLoad}
                                />
                            </ReactCrop>
                        ) : (
                            <span className="loading loading-spinner loading-md my-16 text-primary" />
                        )}
                    </div>

                    <p className="text-xs text-base-content/60">
                        Arraste para escolher o enquadramento. A imagem é reduzida e comprimida ao enviar,
                        gastando menos da cota de armazenamento.
                    </p>
                </div>

                <div className="flex justify-end gap-2 border-t border-base-200 p-4">
                    <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={processing}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={processing || !completedCrop?.width}
                    >
                        {processing ? <span className="loading loading-spinner loading-xs" /> : <MdCrop />}
                        {confirmLabel}
                    </button>
                </div>
            </div>
        </dialog>
    )
}
